import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  Popover,
  TextField,
  Typography,
  Autocomplete,
  Badge,
  Divider,
} from "@mui/material";
import {
  Person,
  CalendarToday,
  FilterList,
  ExpandMore,
} from "@mui/icons-material";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import {
  CALL_OUTCOME_OPTIONS,
  getCallOutcomeLabel,
} from "components/contacts/callOutcomeOptions";
import { COLORS } from "./constants";

const OUTCOMES = CALL_OUTCOME_OPTIONS;

const DATE_PRESETS = () => {
  const today = new Date();
  return [
    {
      label: "Today",
      from: format(today, "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd"),
      dateLabel: `Today (${format(today, "dd MMM")})`,
    },
    {
      label: "This Week",
      from: format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      to: format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      dateLabel: `This Week (${format(startOfWeek(today, { weekStartsOn: 1 }), "dd MMM")} – ${format(endOfWeek(today, { weekStartsOn: 1 }), "dd MMM")})`,
    },
    {
      label: "This Month",
      from: format(startOfMonth(today), "yyyy-MM-dd"),
      to: format(endOfMonth(today), "yyyy-MM-dd"),
      dateLabel: `This Month (${format(startOfMonth(today), "dd MMM")} – ${format(endOfMonth(today), "dd MMM")})`,
    },
  ];
};

const pillSx = {
  borderRadius: "20px",
  height: 36,
  px: 1.5,
  fontWeight: 500,
  fontSize: "0.82rem",
  border: "1.5px solid",
  borderColor: "divider",
  background: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  transition: "all 0.15s",
  "&:hover": { borderColor: COLORS.indigo, color: COLORS.indigo },
};

export default function DashFilterBar({
  users = [],
  statuses = [],
  filters,
  onChange,
  loggedInUserId,
}) {
  const [dateAnchor, setDateAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);

  const isStatusActive = filters.statusId !== "all" || filters.outcome !== "all";

  const activeCount = [
    filters.userId && filters.userId !== loggedInUserId,
    isStatusActive,
  ].filter(Boolean).length;

  const selectedUser = users.find(
    (u) => String(u.id) === String(filters.userId) || String(u.auth_user_id) === String(filters.userId)
  );

  const handlePreset = (preset) => {
    onChange((f) => ({ ...f, fromDate: preset.from, toDate: preset.to, dateLabel: preset.dateLabel }));
    setDateAnchor(null);
  };

  const handleCustomDate = (field, val) => {
    onChange((f) => ({
      ...f,
      [field]: val,
      dateLabel: `Custom (${field === "fromDate" ? val : f.fromDate} – ${field === "toDate" ? val : f.toDate})`,
    }));
  };

  const combinedStatusValue = filters.outcome !== "all" ? filters.outcome : filters.statusId;

  const handleCombinedStatusChange = (e) => {
    const val = e.target.value;
    if (OUTCOMES.some((o) => o.value === val)) {
      onChange((f) => ({ ...f, outcome: val, statusId: "all" }));
    } else {
      onChange((f) => ({ ...f, statusId: val, outcome: "all" }));
    }
  };

  // Strictly filter out any blank/empty status objects returned by the API
  const validStatuses = (statuses || []).reduce((acc, s) => {
    if (!s) return acc;
    const val = typeof s === "string" ? s : (s.id ?? s.name);
    const label = typeof s === "string" ? s : (s.name || s.status_name || s.label);
    if (label && typeof label === "string" && label.trim().length > 0) {
      acc.push({ val, label: label.trim() });
    }
    return acc;
  }, []);

  const getStatusLabel = (id) => {
    if (id === "all") return "All";
    const found = validStatuses.find(s => String(s.val) === String(id));
    return found ? found.label : id;
  };

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap={1.5}
      mb={2.5}
      sx={{
        background: "#fff",
        borderRadius: "14px",
        px: 2,
        py: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Agent selector */}
      <Autocomplete
        size="small"
        options={[{ id: "all", label: "All Agents" }, ...users.map((u) => ({
          id: u.id,
          label: u.username || u.first_name || "Agent",
        }))]}
        value={
          filters.userId === "all"
            ? { id: "all", label: "All Agents" }
            : selectedUser
              ? {
                  id: filters.userId,
                  label: selectedUser.username || selectedUser.first_name || "Agent",
                }
              : { id: "all", label: "All Agents" }
        }
        onChange={(_, val) =>
          onChange((f) => ({ ...f, userId: val?.id ?? "all" }))
        }
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        disableClearable
        sx={{ width: 200 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Agent: All Agents"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Person sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontSize: "0.82rem",
                height: 36,
              },
            }}
          />
        )}
      />

      {/* Date range */}
      <Box
        sx={{ ...pillSx }}
        onClick={(e) => setDateAnchor(e.currentTarget)}
      >
        <CalendarToday sx={{ fontSize: 14 }} />
        <Typography variant="body2" fontWeight={500}>
          {filters.dateLabel || "Select date range"}
        </Typography>
        <ExpandMore sx={{ fontSize: 16, ml: 0.5 }} />
      </Box>
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "12px", p: 2, minWidth: 260 } }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
          PRESETS
        </Typography>
        {DATE_PRESETS().map((p) => (
          <MenuItem
            key={p.label}
            onClick={() => handlePreset(p)}
            sx={{ borderRadius: "8px", mb: 0.5, fontSize: "0.85rem" }}
          >
            {p.label}
          </MenuItem>
        ))}
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
            CUSTOM RANGE
          </Typography>
          <Box display="flex" gap={1}>
            <TextField
              type="date"
              size="small"
              value={filters.fromDate}
              onChange={(e) => handleCustomDate("fromDate", e.target.value)}
              label="From"
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
            <TextField
              type="date"
              size="small"
              value={filters.toDate}
              onChange={(e) => handleCustomDate("toDate", e.target.value)}
              label="To"
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>
        </Box>
      </Popover>

      {/* Unified Status and Outcome Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={combinedStatusValue}
          onChange={handleCombinedStatusChange}
          displayEmpty
          sx={{
            borderRadius: "20px",
            height: 36,
            fontSize: "0.82rem",
            background: "#fff",
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          {validStatuses.map((s) => (
            <MenuItem key={s.val} value={s.val}>
              {s.label}
            </MenuItem>
          ))}
          {validStatuses.length > 0 && <Divider sx={{ my: 0.5 }} />}
          {OUTCOMES.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Filters badge */}
      <Badge badgeContent={activeCount} color="primary">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterList />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            borderRadius: "20px",
            height: 36,
            fontSize: "0.82rem",
            fontWeight: 500,
            borderColor: activeCount > 0 ? COLORS.indigo : "divider",
            color: activeCount > 0 ? COLORS.indigo : "text.secondary",
          }}
        >
          Filters
        </Button>
      </Badge>
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "12px", p: 2, minWidth: 220 } }}
      >
        <Typography variant="body2" fontWeight={600} mb={1}>
          Active Filters
        </Typography>
        {activeCount === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No additional filters active.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {isStatusActive && (
              <Chip
                size="small"
                label={filters.outcome !== "all" ? `Outcome: ${getCallOutcomeLabel(filters.outcome)}` : `Status: ${getStatusLabel(filters.statusId)}`}
                onDelete={() => onChange((f) => ({ ...f, statusId: "all", outcome: "all" }))}
              />
            )}
            {filters.userId && filters.userId !== loggedInUserId && (
              <Chip
                size="small"
                label="Custom Agent"
                onDelete={() => onChange((f) => ({ ...f, userId: loggedInUserId }))}
              />
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
